import {
  faArrowUpRightFromSquare,
  faEnvelope,
  faMessage,
  faPhone
} from '@fortawesome/pro-solid-svg-icons'
import clsx from 'clsx'
import { type ReactElement, type ReactNode, useContext } from 'react'
import { EmailAction } from '~/components/Scopes/Actions/EmailAction'
import { getAsideActionGlobalRef } from '~/components/Scopes/Actions/Global/asideActionRef'
import { MessageAction } from '~/components/Scopes/Actions/MessageAction'
import { getApplicantModalGlobalRef } from '~/components/Scopes/Applicants/ApplicantModal/Global/applicantModalRef'
import type { Action } from '~/domains/actions/types'
import { useApplicantsHead } from '~/domains/applicants/queries/applicantHead'
import { useApplicantPictures } from '~/domains/applicants/queries/applicantPictures'
import type { ApplicantHeadData } from '~/domains/applicants/types'
import { useConfig } from '~/domains/configuration/queries/config'
import { useContactsPictures } from '~/domains/contacts/queries/contactPictures'
import { useContactsHead } from '~/domains/contacts/queries/useContactHead'
import type { ContactHeadData } from '~/domains/contacts/types'
import { useUsersPicture } from '~/domains/users/queries/useUsersPicture'
import { ApplicantModalContext, AsideActionContext } from '~/utils/contextes'
import { formatName } from '~/utils/names'
import { isValidPhoneNumber, phoneDialString } from '~/utils/phone'
import { ParticipantCard, type ParticipantQuickAction } from './ParticipantCard'
import { participantList } from './TaskAside.css'

type ApplicantParticipant = ApplicantHeadData & {
  phoneMobile?: string | null
  phoneSecondary?: string | null
  email?: string
}

type ContactParticipant = ContactHeadData & {
  phone?: string | null
  phoneMobile?: string | null
  email?: string
}

/** SMS offered only to a person reachable on a valid mobile who has not opted out */
const canReceiveSms = (head?: {
  phoneMobile?: string | null
  acceptsMessaging?: boolean
}) =>
  head !== undefined &&
  head.acceptsMessaging !== false &&
  isValidPhoneNumber(head.phoneMobile)

type TaskParticipantsProps = {
  task: Action
  /** Aside restored when a quick action (SMS, email) closes. */
  returnTo?: ReactNode
  /** Called right before a quick action or a profile opening runs. */
  onAction?: () => void
  /** Open quick actions and the applicant modal through the legacy global bridges (no React providers around). */
  legacyBridges?: boolean
  className?: string
}

const getTaskInvolvedUsers = (task: Action) =>
  [...(task.user ? [task.user] : []), ...(task.users ?? [])].filter(
    (user, index, list) =>
      list.findIndex((entry) => entry.id === user.id) === index
  )

export const TaskParticipants = ({
  task,
  returnTo,
  onAction,
  legacyBridges = false,
  className
}: TaskParticipantsProps): ReactElement | null => {
  const { data: legacyUrl = '' } = useConfig('organization.legacy_url')
  const { setAsideAction } = useContext(AsideActionContext)
  const { setApplicantCollection, setApplicantCurrentId } = useContext(
    ApplicantModalContext
  )

  const applicants = Object.values(task.applicants ?? {})
  const contacts = Object.values(task.contacts ?? {})
  const applicantIds = applicants.map((applicant) => applicant.id)
  const contactIds = contacts.map((contact) => contact.id)
  const involvedUsers = getTaskInvolvedUsers(task)

  const applicantsHead = useApplicantsHead<ApplicantParticipant>({
    applicantIds,
    additionalFields: ['phoneMobile', 'phoneSecondary', 'email']
  })
  const contactsHead = useContactsHead<ContactParticipant>({
    contactIds,
    additionalFields: ['phone', 'phoneMobile', 'email']
  })
  const contactsPictures = useContactsPictures({ contactIds })
  const applicantsPictures = useApplicantPictures({ applicantIds })
  const usersPictures = useUsersPicture({
    userIds: involvedUsers.map((user) => user.id)
  })

  const openAside = (action: ReactNode, withReturn = true) => {
    onAction?.()
    if (legacyBridges) {
      getAsideActionGlobalRef().current(action, {
        position: 'sidebar',
        availablePositions: ['sidebar']
      })
      return
    }
    setAsideAction({ action, returnTo: withReturn ? returnTo : undefined })
  }

  const openApplicant = (applicantId: number) => {
    onAction?.()
    if (legacyBridges) {
      getApplicantModalGlobalRef().current(
        applicantId,
        applicants.map((applicant) => applicant.id)
      )
      return
    }
    setApplicantCollection(applicants)
    setApplicantCurrentId(applicantId)
  }

  const cards = [
    ...applicants.map((applicant) => {
      const head = applicantsHead.find(
        ({ data }) => data?.id === applicant.id
      )?.data
      const phone = head?.phoneMobile || head?.phoneSecondary || applicant.phone
      const actions: ParticipantQuickAction[] = []
      if (phone) {
        actions.push({
          key: 'phone',
          label: `Appeler ${phone}`,
          icon: faPhone,
          href: phoneDialString(phone)
        })
      }
      if (canReceiveSms(head)) {
        actions.push({
          key: 'sms',
          label: 'Envoyer un SMS',
          icon: faMessage,
          onClick: () =>
            openAside(<MessageAction applicantIds={[applicant.id]} />)
        })
      }
      if (head?.email || applicant.email) {
        actions.push({
          key: 'email',
          label: 'Envoyer un email',
          icon: faEnvelope,
          onClick: () =>
            openAside(<EmailAction applicantIds={[applicant.id]} />)
        })
      }
      return (
        <ParticipantCard
          key={`applicant-${applicant.id}`}
          name={formatName(applicant)}
          subtitle="Candidat"
          pictureUrl={
            applicantsPictures.find(({ data }) => data?.id === applicant.id)
              ?.data?.profilePicUrl
          }
          onOpen={() => openApplicant(applicant.id)}
          actions={actions}
        />
      )
    }),
    ...contacts.map((contact) => {
      const head = contactsHead.find(
        ({ data }) => data?.id === contact.id
      )?.data
      const phone = head?.phoneMobile || head?.phone || contact.phone
      const actions: ParticipantQuickAction[] = []
      if (phone) {
        actions.push({
          key: 'phone',
          label: `Appeler ${phone}`,
          icon: faPhone,
          href: phoneDialString(phone)
        })
      }
      if (canReceiveSms(head)) {
        actions.push({
          key: 'sms',
          label: 'Envoyer un SMS',
          icon: faMessage,
          onClick: () => openAside(<MessageAction contactIds={[contact.id]} />)
        })
      }
      if (head?.email || contact.email) {
        actions.push({
          key: 'email',
          label: 'Envoyer un email',
          icon: faEnvelope,
          onClick: () =>
            openAside(
              <EmailAction contactsIds={[contact.id]} sendTo="contact" />,
              false
            )
        })
      }
      if (contact.company?.id) {
        actions.push({
          key: 'open',
          label: 'Voir la fiche contact',
          icon: faArrowUpRightFromSquare,
          href: `${legacyUrl}/admin/entreprises/edit/${contact.company.id}#contacts/${contact.id}`,
          newTab: true
        })
      }
      return (
        <ParticipantCard
          key={`contact-${contact.id}`}
          name={formatName(contact)}
          subtitle={contact.company?.name ?? 'Contact'}
          pictureUrl={
            contactsPictures.find(({ data }) => data?.id === contact.id)?.data
              ?.profilePicUrl
          }
          actions={actions}
        />
      )
    }),
    ...involvedUsers.map((user) => (
      <ParticipantCard
        key={`user-${user.id}`}
        name={formatName(user)}
        pictureUrl={
          usersPictures.find(({ data }) => data?.user.id === user.id)?.data
            ?.profilePicUrl
        }
        subtitle={task.user?.id === user.id ? 'Assigné' : 'Participant'}
      />
    ))
  ]

  if (cards.length === 0) {
    return null
  }

  return <div className={clsx(participantList, className)}>{cards}</div>
}
